import { useThemeMode } from "../../../theme/ThemeModeProviderContext";
import { Typography, TypographyProps } from "@mui/material";
import Grid from "@mui/material/Grid";
import React from "react";

export default function Logo({
  product,
  shape,
  size,
  tag,
  tagProps,
  container = <Grid container gap={1} direction="row" />,
  customSize,
}: {
  product: string;
  shape: "square";
  size?: "small" | "medium" | "large";
  tag?: string;
  tagProps?: TypographyProps;
  container?: React.ReactElement<any>; // expect an element we can clone & inject children into
  customSize?: number;
}) {
  // Determine a valid product key or fall back to "Unknown"
  let typedProduct: keyof typeof LOGO_CONSTANTS;
  if (!(product in LOGO_CONSTANTS)) {
    typedProduct = "Unknown";
  } else {
    typedProduct = product as keyof typeof LOGO_CONSTANTS;
  }

  const { mode } = useThemeMode();
  const logosBaseUrl = import.meta.env.LOGOS;
  const logoUrl =
    logosBaseUrl +
    LOGO_CONSTANTS[typedProduct] +
    "-" +
    shape +
    "-" +
    mode +
    ".png";

  // Map size to a pixel width
  let width: number;
  if (customSize) {
    width = customSize;
  } else {
    switch (size) {
      case "medium":
        width = 50;
        break;
      case "large":
        width = 100;
        break;
      default:
        width = 25;
        break;
    }
  }

  // Child content that should live inside the provided container
  const content = (
    <>
      <img
        src={logoUrl}
        alt={product + "-" + shape + "-" + size + "-" + mode}
        width={width}
      />
      {tag && (
        <Typography
          variant="body2"
          // merge passed-in props (allows style / sx overrides)
          {...tagProps}
          // keep vertical centering; if consumer provided sx we merge below
          sx={{ alignSelf: "center", ...(tagProps?.sx || {}) }}
        >
          {tag}
        </Typography>
      )}
    </>
  );

  // If the caller passed a valid React element, clone it and inject the content as children
  if (React.isValidElement(container)) {
    return React.cloneElement(container, {}, content);
  }

  // Fallback: if a non-element sneaks in, just wrap content in a default Grid
  return (
    <Grid container gap={1} direction="row">
      {content}
    </Grid>
  );
}

export const LOGO_CONSTANTS = {
  "Active Directory": "entra",
  AWS: "aws",
  Azure: "azure",
  Copilot: "copilot",
  DataDog: "datadog",
  Dynamics365: "dynamics365",
  Entra: "entra",
  Exchange: "exchange",
  GCP: "gcp",
  Microsoft365: "microsoft365",
  Microsoft: "microsoft",
  OneDrive: "onedrive",
  SharePoint: "sharepoint",
  SHI: "shi",
  "SHI One": "shi",
  Teams: "teams",
  Viva: "viva",
  Unknown: "unknown",
};
